/**
 * NAV SHELL MANIFEST — the app's primary navigation, as data.
 *
 * SHELL PASS A (design pass 7). Lifted verbatim out of pages/UserDashboard.js,
 * where it had been the only copy. Nothing was added, removed, renamed or
 * reordered: this is the same three literals, in the same order, with the same
 * icons and gates. My Desk imports them, so there is still exactly one copy.
 *
 * The shell (pass B) reads this file. A page must not define its own.
 * Route reachability is pinned separately by scripts/check-nav-manifest.js.
 */
import {
  Tag,
  Activity,
  BookOpen,
  Building2,
  CalendarDays,
  ClipboardList,
  DollarSign,
  Droplets,
  FileText,
  HardHat,
  Layers,
  ListTodo,
  Mail,
  Notebook,
  Settings,
  Shield,
  ShoppingCart,
  Smartphone,
  Calculator,
  Sparkles,
  StickyNote,
  Store,
  Timer,
  UserPlus,
  Users
} from 'lucide-react';

export const SIDEBAR_SECTIONS = [
  {
    id: 'connections',
    label: 'Connections',
    icon: Users,
    items: [
      { id: 'pipeline', label: 'Lead Pipeline', icon: UserPlus, path: '/pipeline' },
      { id: 'contacts', label: 'Contacts', icon: Users, path: '/contacts' },
      { id: 'contractors', label: 'Contractors', icon: HardHat, path: '/contacts?view=contractors' },
      { id: 'vendors', label: 'Vendors', icon: Store, path: '/contacts?view=vendors' },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace',
    icon: CalendarDays,
    items: [
      { id: 'tasks', label: 'Tasks', icon: ListTodo },
      { id: 'calendar', label: 'Calendar', icon: CalendarDays },
      { id: 'timetracking', label: 'Time Tracking', icon: Timer },
      { id: 'notes', label: 'Notes', icon: StickyNote },
      // QR Codes lives in the Work Table feature bar (SegmentBar) — see
      // /work-tables/:id → "QR" segment. Removed from Workspace to avoid
      // duplicate entry points.
    ],
  },
  {
    id: 'resources',
    label: 'Resources',
    icon: BookOpen,
    items: [
      { id: 'clips', label: 'Clip Library', icon: BookOpen, path: '/clips' },
      { id: 'paint-library', label: 'Paint Library', icon: Droplets, path: '/paint-library' },
      { id: 'blueprints', label: 'Blueprint Library', icon: BookOpen, path: '/blueprints' },
      { id: 'library', label: 'Studio Library', icon: Notebook, path: '/knowledge' },
      { id: 'library-hub', label: 'Library Hub', icon: Layers, isLibraryHub: true },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: ClipboardList,
    items: [
      { id: 'procurement', label: 'Procurement', icon: ShoppingCart, path: '/procurement' },
      { id: 'documents', label: 'Documents', icon: FileText, path: '/documents' },
      { id: 'site-tags', label: 'Site Tags', icon: Tag, path: '/site-tags' },
      { id: 'survey-templates', label: 'Survey Templates', icon: ClipboardList, path: '/survey-templates' },
      { id: 'scheduled-emails', label: 'Scheduled Emails', icon: Mail, path: '/scheduled-emails' },
      { id: 'all-activity', label: 'All Activity', icon: Activity, path: '/all-activity' },
      { id: 'home-screen', label: 'Put it on your home screen', icon: Smartphone, path: '/settings/home-screen' },
      { id: 'calculator', label: 'Calculator', description: 'Field calculators and converters', icon: Calculator, path: '/tools/calculator' },
      { id: 'quick-start', label: 'Quick Start', description: 'The guided start for a new job', icon: Sparkles, path: '/tools/quick-start' },
    ],
  },
];

// Admin-only tools that require elevated permissions
export const ADMIN_TOOLS = [
  { id: 'financials', label: 'Back of House', icon: DollarSign, path: '/financials', financialOnly: true },
  { id: 'team', label: 'Team', icon: Users, path: '/team', adminOnly: true },
  { id: 'company-settings', label: 'Company Settings', icon: Building2, path: '/settings/company', adminOnly: true },
  { id: 'admin-settings', label: 'Admin Settings', icon: Settings, path: '/settings/admin', adminOnly: true },
];

// Super Admin link (only shown to super admins)
export const SUPER_ADMIN_LINK = { id: 'super-admin', label: 'Super Admin', icon: Shield, path: '/super-admin', superAdminOnly: true };
